import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, ChevronLeft, Play } from "lucide-react";
import { VideoModal } from "@/components/VideoModal";

const line = {
  hidden: { y: "120%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 1, delay: 0.35 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

// Movie partnership showcase — editable later via the admin panel.
// videoUrl links each poster to the same video as its Brand Integration entry.
export const SHOWCASE = [
  {
    src: "/posters/gopal-dhamaal.jpg",
    label: "Gopal Snacks × Dhamaal 4",
    videoUrl: "https://drive.google.com/file/d/16kx5dot4aER-W-BjhyLsOLiqyX_wyIrf/view?usp=sharing",
  },
  {
    src: "/posters/silvercoin-dhurandhar.jpg",
    label: "Silver Coin × Dhurandhar The Revenge",
    videoUrl: "https://drive.google.com/file/d/1-lAZAWQSchzammWwWiyz8Ena0kiBgfSW/view?usp=sharing",
  },
  {
    src: "/posters/blackstone-dhurandhar.jpg",
    label: "Black Stone × Dhurandhar The Revenge",
    videoUrl: "https://drive.google.com/file/d/1A0-W-UHTpMDzqLPK5qgQVzXn8m3Y85er/view?usp=sharing",
  },
  {
    src: "/posters/gopal-filmfare.jpg",
    label: "Gopal Namkeen × Filmfare 2025",
    type: "Event Partnership",
    videoUrl: "https://drive.google.com/file/d/1M8GG5nkrhSuPSJBEfeRYUt8tWYw2vG_i/view?usp=sharing",
  },
];

export const HeroCinematic = () => {
  const ref = useRef(null);
  const spotRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [ghost, setGhost] = useState(null);
  const [playing, setPlaying] = useState(null);

  // Move to another image: the current one becomes a dimmed "ghost" that
  // drifts into the lower-left blank space behind the text.
  const change = (dir) => {
    setGhost({ src: SHOWCASE[index].src, key: Date.now() });
    setIndex((i) => (i + dir + SHOWCASE.length) % SHOWCASE.length);
  };

  useEffect(() => {
    const t = setInterval(() => change(1), 4200);
    return () => clearInterval(t);
  }, [index]);

  const onMove = (e) => {
    const el = spotRef.current;
    if (!el) return;
    const rect = ref.current.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden pt-28 md:pt-32 pb-12" data-testid="hero-section">
      <div className="absolute inset-0 z-0 bg-void" />

      {/* Dimmed outgoing images collecting in the lower-left blank space */}
      <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden hidden lg:block">
        <AnimatePresence>
          {ghost && (
            <motion.img
              key={ghost.key}
              src={ghost.src}
              alt=""
              initial={{ opacity: 0.5, x: 340, y: 30, scale: 1.15 }}
              animate={{ opacity: 0.24, x: 0, y: 0, scale: 1 }}
              exit={{ opacity: 0, transition: { duration: 1 } }}
              transition={{ duration: 1.7, ease: [0.16, 1, 0.3, 1] }}
              className="absolute left-[-3%] bottom-[-4%] w-[60%] h-[72%] object-cover rounded-3xl"
              style={{ filter: "brightness(0.5) saturate(0.9)" }}
            />
          )}
        </AnimatePresence>
      </div>

      <div className="absolute inset-0 z-[2] bg-gradient-to-t from-void via-void/60 to-void/30" />
      {/* Cursor-following cinematic spotlight */}
      <div
        ref={spotRef}
        className="absolute inset-0 z-[3] pointer-events-none hidden md:block mix-blend-soft-light"
        style={{
          background: "radial-gradient(320px circle at var(--mx, 50%) var(--my, 40%), rgba(255,214,140,0.55), rgba(229,9,20,0.12) 45%, transparent 70%)",
        }}
      />

      {/* Content grid: text (left) + stretched showcase box (right) */}
      <div className="container-x w-full relative z-10 grid lg:grid-cols-[46%_minmax(0,1fr)] gap-10 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="text-xs md:text-sm tracking-[0.35em] uppercase font-bold text-gold"
          >
            Creative Production House
          </motion.p>

          <h1 className="mt-6 font-display uppercase text-offwhite leading-[0.92] tracking-tight text-[12vw] sm:text-[10vw] md:text-6xl lg:text-[4.6vw]">
            {["Stories That", "Move Audiences.", "Productions That", "Elevate Brands."].map((t, i) => (
              <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.04em]">
                <motion.span variants={line} custom={i} initial="hidden" animate="show" className={`block ${i >= 2 ? "text-stroke" : ""}`}>
                  {t}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 1 }}
            className="mt-5 md:mt-7 max-w-xl text-sm md:text-lg text-white/60 font-light leading-relaxed"
          >
            Vivek Parekh Production is a creative production house delivering impactful ad films,
            movie collaborations, and celebrity endorsements for brands that want to be remembered.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/movie-integrations" data-testid="hero-view-work" className="group relative inline-flex items-center justify-center gap-2 bg-gold text-void px-9 py-4 uppercase tracking-[0.2em] text-xs font-semibold overflow-hidden">
              <span className="relative z-10 flex items-center gap-2">View Our Work <ArrowUpRight size={16} /></span>
              <span className="absolute inset-0 bg-champagne scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            </Link>
            <a
              href="#contact"
              data-testid="hero-discuss-project"
              onClick={(e) => { e.preventDefault(); const el = document.getElementById("contact"); if (el) (window.__lenis?.scrollTo?.(el, { offset: -80 })) || el.scrollIntoView({ behavior: "smooth" }); }}
              className="group inline-flex items-center justify-center gap-2 border border-white/25 text-offwhite px-9 py-4 uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold transition-colors duration-300 cursor-pointer"
            >
              Discuss a Project
            </a>
          </motion.div>
        </div>

        {/* Stretched showcase box with auto-rotating images */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full h-[48vh] lg:h-[62vh] max-h-[600px] rounded-3xl border border-gold/40 overflow-hidden mt-4 lg:mt-0 bg-void"
          data-testid="hero-showcase-box"
          style={{ boxShadow: "0 0 0 1px rgba(212,175,55,0.15), 0 0 40px 0 rgba(212,175,55,0.18), inset 0 0 40px rgba(0,0,0,0.4)" }}
        >
          <AnimatePresence>
            <motion.img
              key={index}
              src={SHOWCASE[index].src}
              alt={SHOWCASE[index].label}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 w-full h-full object-contain"
            />
          </AnimatePresence>

          {/* Play button — opens the same video as this poster's Brand Integration */}
          {SHOWCASE[index].videoUrl && (
            <button
              onClick={() => setPlaying(SHOWCASE[index])}
              data-testid="hero-showcase-play"
              aria-label={`Play ${SHOWCASE[index].label} video`}
              className="absolute inset-0 z-[8] flex items-center justify-center group/play"
            >
              <span className="w-20 h-20 rounded-full border border-gold/70 bg-void/40 backdrop-blur-sm flex items-center justify-center transition-all duration-300 group-hover/play:bg-gold group-hover/play:scale-110 shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
                <Play size={30} className="text-gold group-hover/play:text-void ml-1 transition-colors" fill="currentColor" />
              </span>
            </button>
          )}
          {/* Partnership label — solid coloured chip */}
          <div className="absolute left-5 bottom-5 z-10 max-w-[80%]">
            <div className="inline-block bg-gold px-5 py-3 rounded-md shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
              <p className="text-[0.6rem] tracking-[0.35em] uppercase text-void/70 font-semibold">{SHOWCASE[index].type || "Movie Partnership"}</p>
              <AnimatePresence mode="wait">
                <motion.h3
                  key={index}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.5 }}
                  className="mt-1 font-display text-xl md:text-2xl xl:text-3xl uppercase text-void leading-[0.95]"
                >
                  {SHOWCASE[index].label}
                </motion.h3>
              </AnimatePresence>
            </div>
          </div>

          {/* Prev / next chevrons to navigate images */}
          <div className="absolute bottom-5 right-5 z-10 flex items-center gap-3">
            <button
              onClick={() => change(-1)}
              data-testid="hero-showcase-prev"
              aria-label="Previous image"
              className="w-12 h-12 rounded-full border border-gold/50 bg-void/40 backdrop-blur-sm flex items-center justify-center text-gold hover:bg-gold hover:text-void transition-colors duration-300"
            >
              <ChevronLeft size={22} strokeWidth={2.5} />
            </button>
            <button
              onClick={() => change(1)}
              data-testid="hero-showcase-next"
              aria-label="Next image"
              className="w-12 h-12 rounded-full border border-gold/50 bg-void/40 backdrop-blur-sm flex items-center justify-center text-gold hover:bg-gold hover:text-void transition-colors duration-300"
            >
              <ChevronRight size={22} strokeWidth={2.5} />
            </button>
          </div>
        </motion.div>
      </div>

      <VideoModal open={!!playing} url={playing?.videoUrl} title={playing?.label} onClose={() => setPlaying(null)} />
    </section>
  );
};
